import { Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import React, { useEffect, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import Input from 'components/forms/Input'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import Select from 'components/forms/Select'
import { useDispatch } from 'react-redux'
import ToastrAction from 'toastr/actions/ToastrAction'
import { isEmpty, max } from 'lodash'
import SimpleDatePicker from '../../../../components/forms/SimpleDatePicker'
import { CardTable } from '../../../../components/datatable/NewTable'
import { mainBlue } from '../../../../utils/constants/ColorTheme'

const getAmendmentHeaders = () => [
    { key: 'number', value: i18n.amendmentNumber, noSort: true },
    { key: 'effectiveDate', value: i18n.amendmentEffectiveDate, noSort: true },
    { key: 'title', value: i18n.amendmentTitle, noSort: true },
]

// Une convention porte ses avenants dans `amendments` (côté front uniquement) : ils sont
// mis à plat dans link_conventionAmendments à l'enregistrement de l'entité de gestion.
const DialogConventions = ({
    open= false,
    setOpen= () => {},
    managementUnitCode = -1,

    conventions=[],
    setConventions= () => {},

    defaultValue= {},
    setDefaultValue= () => {},
    isNew= false,
}) => {
    const dispatch = useDispatch()
    const contractualLinks = useSandreList(SANDRE.CONVENTIONS_LIEN)

    const [convention, setConvention] = useState({})

    useEffect(() => {
        if (open) setConvention(isNew ? { amendments: [] } : { ...defaultValue, amendments: defaultValue.amendments ?? [] })
    }, [open, isNew, defaultValue])

    const change = changes => setConvention(prev => ({ ...prev, ...changes }))

    const amendments = convention.amendments ?? []
    const changeAmendment = (index, changes) => change({ amendments: amendments.map((a, i) => (i === index ? { ...a, ...changes } : a)) })

    const onClose = () => {
        setConvention({})
        setDefaultValue({})
        setOpen(false)
    }

    const onValidate = () => {
        if (isEmpty(convention.convention)) {
            dispatch(ToastrAction.warning(i18n.pleaseCompleteAllRequiredField))
            return
        }
        if (isNew) {
            const conventionId = (max(conventions.map(c => c.conventionId)) ?? 0) + 1
            setConventions([...conventions, { ...convention, managementUnitCode, conventionId }])
        } else {
            setConventions(conventions.map(conv => (conv.conventionId === defaultValue.conventionId ? { ...convention, managementUnitCode } : conv)))
        }
        onClose()
    }

    const amendmentRows = amendments.map((amendment, index) => ({
        index,
        number: { value: <Input value={amendment.number} onChange={number => changeAmendment(index, { number })} /> },
        effectiveDate: { value: <SimpleDatePicker value={amendment.effectiveDate} onChange={effectiveDate => changeAmendment(index, { effectiveDate })} /> },
        title: { value: <Input value={amendment.title} onChange={title => changeAmendment(index, { title })} /> },
    }))

    return (
        <Dialog open={open} maxWidth={'lg'} fullWidth>
            <DefaultDialogTitle title={isNew ? i18n.addAgreement : i18n.updateAgreement} onClose={onClose}/>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid size={4}>
                        <Input
                            title={ i18n.agreement }
                            value={ convention.convention }
                            onChange={value => change({ convention: value })}
                            obligatory
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.contractualRelationship}
                            options={contractualLinks}
                            value={ convention.contractualLink }
                            onChange={contractualLink => change({ contractualLink })}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={ i18n.providerName }
                            value={ convention.providerName }
                            onChange={providerName => change({ providerName })}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Input
                            title={ i18n.missionNature }
                            value={ convention.characteristics }
                            onChange={characteristics => change({ characteristics })}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SimpleDatePicker
                            label={i18n.contractStartDate}
                            value={convention.startDate}
                            onChange={startDate => change({ startDate })}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SimpleDatePicker
                            label={i18n.initialContractEndDate}
                            value={convention.initialEndDate}
                            onChange={initialEndDate => change({ initialEndDate })}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SimpleDatePicker
                            label={i18n.effectiveContractEndDate}
                            value={convention.effectiveEndDate}
                            onChange={effectiveEndDate => change({ effectiveEndDate })}
                        />
                    </Grid>
                    <Grid size={12}>
                        <CardTable
                            title={i18n.amendments}
                            headers={getAmendmentHeaders()}
                            rows={amendmentRows}
                            color={mainBlue}
                            rowsPerPageOptions={[{ value: 1000, label: '1000' }]}
                            actions={[{
                                icon: 'add',
                                color: 'white',
                                tooltip: i18n.addAmendment,
                                onClick: () => change({ amendments: [...amendments, {}] }),
                            }]}
                            lineActions={[{
                                icon: 'delete',
                                onClick: row => change({ amendments: amendments.filter((_, i) => i !== row.index) }),
                            }]}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onValidate}
                    variant='contained'
                    color='primary'
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const amendmentShape = PropTypes.shape({
    number: PropTypes.string,
    effectiveDate: PropTypes.number,
    title: PropTypes.string,
})

const conventionShape = PropTypes.shape({
    convention: PropTypes.string,
    conventionId: PropTypes.number,
    characteristics: PropTypes.string,
    contractualLink: PropTypes.number,
    providerName: PropTypes.string,
    startDate: PropTypes.number,
    initialEndDate: PropTypes.number,
    effectiveEndDate: PropTypes.number,
    amendments: PropTypes.arrayOf(amendmentShape),
    managementUnitCode: PropTypes.number,
})

DialogConventions.propTypes = {
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    managementUnitCode: PropTypes.number,
    defaultValue: conventionShape,
    setDefaultValue: PropTypes.func,
    conventions: PropTypes.arrayOf(conventionShape),
    setConventions: PropTypes.func,
    isNew: PropTypes.bool,
    setIsNew: PropTypes.func,
}

export default DialogConventions
